import { configureChains, createClient, WagmiConfig } from "wagmi"
import { BigNumber, constants } from "ethers"
import { infuraProvider } from "wagmi/providers/infura"
import { jsonRpcProvider } from "wagmi/providers/jsonRpc"
import { ReactNode } from "react"

import { connectorsForWallets } from "@rainbow-me/rainbowkit"
import {
  injectedWallet,
  rainbowWallet,
  walletConnectWallet,
  coinbaseWallet,
  metaMaskWallet,
} from "@rainbow-me/rainbowkit/wallets"
import { isMaster } from "@/util/constants/server"
import { baseChain, baseSepolia, zoraChain } from "@/util/crypto"

import {
  mainnet,
  polygon,
  goerli,
  polygonMumbai,
  optimism,
  optimismGoerli,
  base,
  zora,
  zoraTestnet,
} from "viem/chains"

/**
 * baseGoerli is deprecated so define baseSepolia here.
 * Ideally we would import from viem/chains but that would mean we'd need to upgrade
 * and thus introduce many breaking changes.
 */

import type { Chain } from "wagmi"

// API key for Ethereum node
// Two popular services are Infura (infura.io) and Alchemy (alchemy.com)
export const infuraId = "84566032b8074828961a55373c793e52"

export const alchemyId = "h0Uc6vitnXs8a5DoS3EZm6SBbsplkUqj"

console.log(
  "Are we on vercel?",
  isMaster,
  process.env.VERCEL,
  process.env.NEXT_PUBLIC_VERCEL,
  process.env.VERCEL_ENV,
  process.env.NEXT_PUBLIC_VERCEL_ENV,
  process.env.NEXT_FORCE_PROD_CHAINS
)

// Force Wagmi to use prod chains in prod, and
// test chains when not on Vercel.
const _chains: Array<Chain> =
  isMaster || process.env.NEXT_PUBLIC_FORCE_PROD_CHAINS
    ? [mainnet, polygon, optimism, base, zora]
    : [goerli, polygonMumbai, optimismGoerli, baseSepolia, zoraTestnet]

/**
 * Given a Wagmi chain, returns the Ankr network name to use in the
 * RPC URL.
 */
function getAnkrNetwork(
  chain:
    | (typeof mainnet)["network"]
    | (typeof polygon)["network"]
    | (typeof optimism)["network"]
    | (typeof base)["network"]
    | (typeof goerli)["network"]
    | (typeof polygonMumbai)["network"]
    | (typeof optimismGoerli)["network"]
    | (typeof baseSepolia)["network"]
) {
  switch (chain) {
    case "matic":
      return "polygon"
    case "maticmum":
      return "polygon_mumbai"
    case "homestead":
      return "eth"
    case "goerli":
      return "eth_goerli"
    case "optimism":
      return "optimism"
    case "optimism-goerli":
      return "optimism_testnet"
    //case "base":
    //  return "base"
    //case "base-goerli":
    //  return "base_goerli"
    default:
      console.warn("Incorrect chain " + chain + " passed to getAnkrNetwork.")
      return undefined
  }
}

const zoraChainRpc =
  isMaster || process.env.NEXT_PUBLIC_FORCE_PROD_CHAINS
    ? zoraChain
    : zoraTestnet
const baseChainRpc =
  isMaster || process.env.NEXT_PUBLIC_FORCE_PROD_CHAINS
    ? "base-mainnet.g.alchemy.com/v2/h0Uc6vitnXs8a5DoS3EZm6SBbsplkUqj"
    : "base-sepolia.g.alchemy.com/v2/KGurH2kIqi96hYT0c62wKu-eNjhfbqxp"

export const { chains, provider } = configureChains(_chains, [
  infuraProvider({ apiKey: infuraId }),

  jsonRpcProvider({
    rpc: (chain) => {
      if (chain.id !== baseChain.id) return null

      return {
        http: `https://${baseChainRpc}`,
        webSocket: `wss://${baseChainRpc}`,
      }
    },
  }),

  jsonRpcProvider({
    rpc: (chain) => {
      if (chain.id !== baseChain.id) return null

      return {
        http: "https://mainnet.base.org",
      }
    },
  }),

  // Need to specify this for Zora chain as well
  jsonRpcProvider({
    rpc: (chain) => {
      if (chain.id !== zoraChain.id) return null

      return {
        http: zoraChainRpc.rpcUrls.default.http[0],
        webSocket: zoraChainRpc.rpcUrls.default.webSocket[0],
      }
    },
  }),

  // Need to specify this for Base, since there's no Base RPC URL provided by
  // Infura.
  jsonRpcProvider({
    rpc: (chain) => {
      // @ts-ignore
      const network = getAnkrNetwork(chain.network)

      if (!network) return null
      return {
        http: "https://mainnet.base.org",
      }
    },
  }),
])

/**
 * WalletConnect v2 project ID
 */
const projectId = "1c3b64d40de34f18b135f597aae251fa"

const connectors = connectorsForWallets([
  {
    groupName: "Recommended",
    wallets: [
      metaMaskWallet({ chains, projectId }),
      injectedWallet({ chains }),
      coinbaseWallet({ appName: "Paragraph", chains }),
      rainbowWallet({ projectId, chains }),
      walletConnectWallet({
        projectId,
        chains,
      }),
    ],
  },
])

export const wagmiClient = createClient({
  autoConnect: true,
  connectors,
  provider,
})

const WagmiProvider = ({ children }: { children: ReactNode }) => {
  return <WagmiConfig client={wagmiClient}>{children}</WagmiConfig>
}

export default WagmiProvider

export function formatBalance(balance: BigNumber, totalAmount: BigNumber) {
  if (totalAmount.eq(constants.MaxUint256)) {
    return {
      balance: "∞",
      totalAmount: "∞",
    }
  }
  return {
    balance: balance.toString(),
    totalAmount: totalAmount.toString(),
  }
}

function extractChainInfo(inputString: string): [string, string] | null {
  const regex = /Expected "([\w\d\s]+)", received "([\w\d\s]+)"/
  const match = inputString.match(regex)

  if (!match) {
    console.log("er nothing")
    return null
  }
  const expected = match?.[1]
  const received = match?.[2]

  if (!expected || !received) return null

  return [expected, received]
}

// Checks if a given wagmi error is due to an insufficient funds error code.
function checkInsufficientFundsErrorCode(error: Error): boolean {
  return (
    error &&
    "code" in error &&
    error.code == -32603 &&
    "data" in error &&
    // @ts-ignore
    "code" in error.data &&
    error.data.code == -32000
  )
}

export enum WagmiErrorType {
  InsufficientBalance = "INSUFFICIENT_BALANCE",
  WrongChain = "WRONG_CHAIN",
  Unknown = "UNKNOWN",
}

/**
 * Parsed Wagmi error object that contains the type of error and display strings.
 */
export type ParsedWagmiError = {
  type: WagmiErrorType
  msg: string
  msgShort: string
}

/**
 * Parses a Wagmi error and makes it into something useful.
 * @param error The Wagmi error you want parsed.
 * @returns Returns the type of error, as well as a long and short display string for presentation to the user.
 */
export function ParseWagmiError(error: Error): ParsedWagmiError {
  const originalErrMsg =
    // @ts-ignore
    error?.data?.message ||
    // @ts-ignore
    error?.reason ||
    error?.message ||
    JSON.stringify(error)

  const insufficientFundsMessage = ["insufficient funds", "Not enough funds"]

  for (const msg of insufficientFundsMessage) {
    if (originalErrMsg?.includes(msg)) {
      return {
        type: WagmiErrorType.InsufficientBalance,
        msg: "You don't have enough funds in your wallet.",
        msgShort: "Insufficient balance",
      }
    }
  }

  if (checkInsufficientFundsErrorCode(error)) {
    return {
      type: WagmiErrorType.InsufficientBalance,
      msg: "You don't have enough funds in your wallet.",
      msgShort: "Insufficient balance",
    }
  }

  const chainMismatch = extractChainInfo(originalErrMsg)

  if (chainMismatch) {
    return {
      type: WagmiErrorType.WrongChain,
      msg:
        "You're connected to the wrong chain. Switch to " +
        chainMismatch[0] +
        " to collect.",
      msgShort: "Wrong chain",
    }
  }

  return {
    type: WagmiErrorType.Unknown,
    msg: originalErrMsg,
    msgShort: "Unknown error",
  }
}
